import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Typography,
  Button,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Warning,
  Error,
  Info,
  Close,
  Build
} from '@mui/icons-material';
import { useEmergency } from '../../hooks/useEmergency';

interface EmergencyAlertProps {
  showOnNormal?: boolean;
  autoRefresh?: boolean;
  onDismiss?: () => void;
  dismissible?: boolean;
}

const EmergencyAlert: React.FC<EmergencyAlertProps> = ({
  showOnNormal = false,
  autoRefresh = true,
  onDismiss,
  dismissible = false
}) => {
  const { status, isEmergencyActive, emergencyLevel, maintenanceMode } = useEmergency(autoRefresh, 60000);
  const [dismissed, setDismissed] = React.useState(false);

  if (!status || dismissed) return null;

  // Don't show if emergency is not active and showOnNormal is false
  if (!isEmergencyActive && !maintenanceMode && !showOnNormal) {
    return null;
  }

  const getSeverity = () => {
    if (maintenanceMode) return 'info';
    switch (emergencyLevel) {
      case 'critical':
      case 'lockdown':
        return 'error';
      case 'warning':
        return 'warning';
      case 'normal':
        return 'success';
      default:
        return 'info';
    }
  };

  const getIcon = () => {
    if (maintenanceMode) return <Build />;
    switch (emergencyLevel) {
      case 'critical':
      case 'lockdown':
        return <Error />;
      case 'warning':
        return <Warning />;
      case 'normal':
        return <Info />;
      default:
        return <Info />;
    }
  };

  const getTitle = () => {
    if (maintenanceMode) return 'System Maintenance';
    if (isEmergencyActive) return `Emergency Level: ${emergencyLevel.toUpperCase()}`;
    return 'System Status: Normal';
  };

  const getMessage = () => {
    if (maintenanceMode && status.emergency_message) {
      return status.emergency_message;
    }
    if (isEmergencyActive && status.emergency_message) {
      return status.emergency_message;
    }
    if (maintenanceMode) {
      return 'The system is currently under maintenance. Some features may be temporarily unavailable.';
    }
    if (isEmergencyActive) {
      return 'Emergency security measures are currently active. Some system features may be restricted.';
    }
    return 'All systems are operating normally.';
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const getActiveBlocks = () => {
    const blocks = [];
    const activeBlocks = status.active_blocks;
    
    if (activeBlocks.registration) blocks.push('Registration');
    if (activeBlocks.login) blocks.push('Login');
    if (activeBlocks.all_api) blocks.push('API Access');
    if (activeBlocks.post_creation) blocks.push('Post Creation');
    if (activeBlocks.content_generation) blocks.push('Content Generation');
    if (activeBlocks.social_posting) blocks.push('Social Posting');
    if (activeBlocks.file_uploads) blocks.push('File Uploads');
    
    return blocks;
  };

  const activeBlocks = getActiveBlocks();

  return (
    <Collapse in={!dismissed}>
      <Alert
        severity={getSeverity() as any}
        icon={getIcon()}
        action={
          dismissible && (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <Close fontSize="inherit" />
            </IconButton>
          )
        }
        sx={{ mb: 2 }}
      >
        <AlertTitle>{getTitle()}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {getMessage()}
        </Typography>
        
        {activeBlocks.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
              Active Restrictions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {activeBlocks.map((block) => (
                <Chip
                  key={block}
                  label={block}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}


      </Alert>
    </Collapse>
  );
};

export default EmergencyAlert; 